"use client";

import { useCallback, useEffect, useState } from "react";
import {
  CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw, Cpu, Cloud,
  MonitorCheck, ShieldCheck,
} from "lucide-react";

/**
 * Zero-install, fully client-side compatibility probe.
 *
 * It runs the SAME check the askFinz extension runs at model-activation time
 * (see extension `src/ui/lib/localAi.ts` → checkWebGPU): request a WebGPU
 * adapter and reject SOFTWARE adapters (SwiftShader / lavapipe / llvmpipe /
 * "Microsoft Basic Render"). A hardware adapter ⇒ on-device AI works; a
 * software adapter or no WebGPU ⇒ the extension falls back to credit-metered
 * cloud models. Nothing is installed, and no result leaves the browser.
 */

type Status = "pass" | "warn" | "fail" | "pending";
type Verdict = "compatible" | "cloud" | "running" | "error";

interface CheckRow {
  key: string;
  label: string;
  status: Status;
  detail: string;
}

function detectBrowser(): { name: string; ok: boolean } {
  if (typeof navigator === "undefined") return { name: "Unknown", ok: false };
  const ua = navigator.userAgent;
  // Order matters: Edge and Brave/Opera contain "Chrome" too.
  if (/Edg\//.test(ua)) return { name: "Microsoft Edge", ok: true };
  if (/OPR\//.test(ua)) return { name: "Opera", ok: true };
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return { name: "Google Chrome", ok: true };
  if (/Chromium\//.test(ua)) return { name: "Chromium", ok: true };
  if (/Firefox\//.test(ua)) return { name: "Firefox", ok: false };
  if (/Safari\//.test(ua)) return { name: "Safari", ok: false };
  return { name: "Your browser", ok: false };
}

const SOFTWARE_RX = /swiftshader|llvmpipe|lavapipe|basic render|software|microsoft basic|warp/i;

export function WebGpuCheck() {
  const [verdict, setVerdict] = useState<Verdict>("running");
  const [rows, setRows] = useState<CheckRow[]>([]);
  const [gpuLabel, setGpuLabel] = useState<string>("");

  const run = useCallback(async () => {
    setVerdict("running");
    setRows([]);
    setGpuLabel("");

    const out: CheckRow[] = [];
    const push = (r: CheckRow) => { out.push(r); setRows([...out]); };

    // 1. Secure context (WebGPU requires HTTPS)
    const secure = typeof window !== "undefined" && window.isSecureContext;
    push({
      key: "secure", label: "Secure connection", status: secure ? "pass" : "fail",
      detail: secure ? "Served over HTTPS." : "This check needs a secure (HTTPS) page.",
    });

    // 2. Browser
    const br = detectBrowser();
    push({
      key: "browser", label: "Browser", status: br.ok ? "pass" : "warn",
      detail: br.ok
        ? `${br.name} — supports on-device AI.`
        : `${br.name} — on-device AI needs a Chromium browser (Chrome or Edge).`,
    });

    const nav = navigator as any;

    // 3. WebGPU API present
    if (!nav.gpu) {
      push({
        key: "webgpu", label: "Graphics support", status: "fail",
        detail: "This browser can't use your graphics processor, so models can't run on your device.",
      });
      setVerdict("cloud");
      return;
    }
    push({ key: "webgpu", label: "Graphics support", status: "pass", detail: "Your browser supports accelerated graphics." });

    // 4. Graphics adapter
    let adapter: any = null;
    try {
      adapter = await nav.gpu.requestAdapter({ powerPreference: "high-performance" });
    } catch (e: any) {
      push({
        key: "adapter", label: "Graphics adapter", status: "fail",
        detail: `Could not query the GPU (${e?.message || "error"}).`,
      });
      setVerdict("error");
      return;
    }
    if (!adapter) {
      push({
        key: "adapter", label: "Graphics adapter", status: "fail",
        detail: "No graphics adapter was offered to the browser.",
      });
      setVerdict("cloud");
      return;
    }

    // Adapter info (API has shifted across Chrome versions — try each shape)
    let info: any = {};
    try { info = adapter.info || {}; } catch { /* ignore */ }
    if ((!info || !info.vendor) && typeof adapter.requestAdapterInfo === "function") {
      try { info = await adapter.requestAdapterInfo(); } catch { /* ignore */ }
    }
    const infoStr = [info.vendor, info.architecture, info.device, info.description]
      .filter(Boolean).join(" ");
    if (infoStr) setGpuLabel(infoStr);

    push({
      key: "adapter", label: "Graphics adapter", status: "pass",
      detail: infoStr ? `Found: ${infoStr}` : "A graphics adapter is available.",
    });

    // 5. Hardware vs software — THE decisive check
    const isSoftware = adapter.isFallbackAdapter === true || SOFTWARE_RX.test(infoStr);
    push({
      key: "hardware", label: "Hardware acceleration", status: isSoftware ? "fail" : "pass",
      detail: isSoftware
        ? "The adapter is software-only — a model would run on the CPU and never finish. On-device AI is disabled."
        : "Backed by a real GPU — on-device AI can run here.",
    });

    // 6. Fast-math (f16) — informational, affects which local models fit
    let f16 = false;
    try { f16 = !!adapter.features?.has?.("shader-f16"); } catch { /* ignore */ }
    if (!isSoftware) {
      push({
        key: "f16", label: "Fast-math (f16) support", status: f16 ? "pass" : "warn",
        detail: f16
          ? "Supported — the widest range of local models will run."
          : "Not supported — local models still run, using slightly larger variants.",
      });
    }

    setVerdict(isSoftware ? "cloud" : "compatible");
  }, []);

  useEffect(() => { run(); }, [run]);

  const running = verdict === "running";

  return (
    <div className="rounded-2xl border hairline-strong overflow-hidden bg-background">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 p-6 md:p-8 border-b hairline">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border hairline-strong">
            <MonitorCheck className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-lg font-medium tracking-tight">Live compatibility check</h3>
            <p className="mt-0.5 text-[13px] text-foreground/60 inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" />
              Runs entirely in your browser — nothing is installed or sent anywhere.
            </p>
          </div>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="shrink-0 inline-flex items-center gap-2 rounded-full border hairline-strong px-4 py-2 text-sm font-medium hover:bg-foreground/5 disabled:opacity-50"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {running ? "Checking…" : "Run again"}
        </button>
      </div>

      {/* Verdict banner — `running` already excludes the "running" verdict. */}
      {!running && <VerdictBanner verdict={verdict} gpuLabel={gpuLabel} />}

      {/* Checklist */}
      <ul className="divide-y hairline">
        {rows.map((r) => (
          <li key={r.key} className="flex items-start gap-3 px-6 md:px-8 py-3.5">
            <StatusIcon status={r.status} />
            <div className="min-w-0">
              <div className="text-sm font-medium">{r.label}</div>
              <div className="text-[13px] text-foreground/65 leading-relaxed">{r.detail}</div>
            </div>
          </li>
        ))}
        {running && (
          <li className="flex items-center gap-3 px-6 md:px-8 py-3.5 text-foreground/50 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Probing your graphics processor…
          </li>
        )}
      </ul>
    </div>
  );
}

function StatusIcon({ status }: { status: Status }) {
  if (status === "pass") return <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />;
  if (status === "fail") return <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-500" />;
  return <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin text-foreground/40" />;
}

function VerdictBanner({ verdict, gpuLabel }: { verdict: Verdict; gpuLabel: string }) {
  if (verdict === "compatible") {
    return (
      <div className="flex items-start gap-3 px-6 md:px-8 py-5 bg-emerald-500/10 border-b border-emerald-500/20">
        <Cpu className="h-5 w-5 mt-0.5 shrink-0 text-emerald-500" />
        <div>
          <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            Compatible — on-device AI will run here.
          </p>
          <p className="mt-1 text-[13px] text-foreground/70 leading-relaxed">
            {gpuLabel ? <>Your graphics processor (<span className="font-medium">{gpuLabel}</span>) can run models locally, at no usage cost.</> : "Your graphics processor can run models locally, at no usage cost."}
          </p>
        </div>
      </div>
    );
  }
  if (verdict === "error") {
    return (
      <div className="flex items-start gap-3 px-6 md:px-8 py-5 bg-amber-500/10 border-b border-amber-500/20">
        <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-amber-500" />
        <div>
          <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
            Couldn&#39;t finish the check.
          </p>
          <p className="mt-1 text-[13px] text-foreground/70 leading-relaxed">
            Something blocked the graphics query. Try “Run again”, or check that hardware
            acceleration is enabled in your browser settings.
          </p>
        </div>
      </div>
    );
  }
  // cloud
  return (
    <div className="flex items-start gap-3 px-6 md:px-8 py-5 bg-amber-500/10 border-b border-amber-500/20">
      <Cloud className="h-5 w-5 mt-0.5 shrink-0 text-amber-500" />
      <div>
        <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
          On-device AI isn&#39;t available on this device — cloud models will be used.
        </p>
        <p className="mt-1 text-[13px] text-foreground/70 leading-relaxed">
          The extension still works fully. Local models run in the cloud instead, drawing from
          the credits on your askFinz account. See the steps below to enable on-device AI.
        </p>
      </div>
    </div>
  );
}
